/**
 * Office Section Component
 * Content tagged with #theoffice
 * Similar behavior to Gym section with mixed media
 */

import React, { useState } from 'react';
import MediaEmbed from './MediaEmbed';
import { useSwipeGesture } from '../hooks/useSwipeGesture';
import { useAutoplay } from '../context/AutoplayContext';

const OfficeSection = ({ content = [] }) => {
  const [mobileIndex, setMobileIndex] = useState(0);
  const { shouldAutoplay } = useAutoplay();
  const isAutoplay = shouldAutoplay('office-anchor');

  const handleSwipe = (direction) => {
    if (direction === 'up' || direction === 'left') {
      setMobileIndex((prev) => Math.min(prev + 1, content.length - 1));
    } else if (direction === 'down' || direction === 'right') {
      setMobileIndex((prev) => Math.max(prev - 1, 0));
    }
  };

  // Standardized swipe distance of 40px for better UX
  const swipeHandlers = useSwipeGesture(handleSwipe, {
    minSwipeDistance: 40,
  });

  if (!content || content.length === 0) {
    return (
      <section
        id="office-anchor"
        className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-100 to-lavender-100"
      >
        <div className="text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">The Office</h2>
          <p className="text-gray-600">No content available yet</p>
        </div>
      </section>
    );
  }

  return (
    <section
      id="office-anchor"
      className="min-h-screen bg-gradient-to-br from-purple-100 to-lavender-100 py-12 md:py-20"
    >
      <div className="max-w-7xl mx-auto px-4">
        {/* Section header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            The Office 💼
          </h2>
          <p className="text-lg text-gray-600">
            Work life, productivity tips, and professional journey
          </p>
        </div>

        {/* Desktop: Grid layout with variation */}
        <div className="hidden md:grid md:grid-cols-4 gap-4">
          {content.map((item, index) => {
            // Create varied layout
            const getGridClass = () => {
              if (index === 0) return 'col-span-2 row-span-2';
              if (index === 3) return 'col-span-2 row-span-1';
              return 'col-span-1 row-span-1';
            };

            return (
              <div
                key={item.id}
                className={`
                  rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow
                  ${getGridClass()}
                `}
              >
                <MediaEmbed
                  item={item}
                  aspectRatio={index === 0 ? '3/4' : '1/1'}
                  autoplay={index === 0 && isAutoplay}
                />
              </div>
            );
          })}
        </div>

        {/* Mobile: Swipe queue */}
        <div className="md:hidden relative" {...swipeHandlers}>
          <div className="aspect-[9/16] max-h-[70vh] mx-auto rounded-2xl overflow-hidden shadow-2xl">
            {content[mobileIndex] && (
              <MediaEmbed
                item={content[mobileIndex]}
                aspectRatio="9/16"
                autoplay={mobileIndex === 0 && isAutoplay}
              />
            )}
          </div>

          {/* Navigation dots */}
          <div className="flex justify-center gap-2 mt-6">
            {content.map((_, index) => (
              <button
                key={index}
                onClick={() => setMobileIndex(index)}
                className={`
                  w-2 h-2 rounded-full transition-all
                  ${index === mobileIndex ? 'bg-purple-500 w-8' : 'bg-purple-200'}
                `}
                aria-label={`Go to item ${index + 1}`}
              />
            ))}
          </div>

          {/* Swipe hint */}
          <p className="text-center text-gray-400 text-sm mt-4">
            Swipe up or left for next
          </p>
        </div>
      </div>
    </section>
  );
};

export default OfficeSection;
